import { providesList } from '@helpers/providerList';
import BaseService from '../base';
import {
  AccessesPerformanceApiProps,
  AccessParamsProps,
  AccessesApiProps,
  AccessPerformanceParams,
  AccessStaffingTableParams,
  AccessesStaffingTableApiProps,
  AccessByDocumentParams,
  SectionAccessParamsProps,
  AccessContractorPerformanceParams,
  AccessAreaSectionPerformanceParams,
  EventsParams,
  EventsApiProps,
  CreateManualAccessParams,
  UpdateManualAccessParams,
  DeleteManualAccessParams,
} from './types';

class Access extends BaseService {
  getAccessPerformance() {
    return {
      query: ({ siteId, params }: AccessPerformanceParams) => ({
        url: `sites/${siteId}/access_performance`,
        params,
      }),
      providesTags: (result?: AccessesPerformanceApiProps) => providesList(
        result?.accessPerformance?.daily,
        this.tagType,
      ),
    };
  }

  getContractorAccessPerformance() {
    return {
      query: ({ contractorId, params }: AccessContractorPerformanceParams) => ({
        url: `contractors/${contractorId}/access_performance`,
        params,
      }),
      providesTags: (result?: AccessesPerformanceApiProps) => providesList(
        result?.accessPerformance?.daily,
        this.tagType,
      ),
    };
  }

  getAreaSectionAccessPerformance() {
    return {
      query: ({ sectionId, params }: AccessAreaSectionPerformanceParams) => ({
        url: `sections/${sectionId}/access_performance`,
        params,
      }),
      providesTags: (result?: AccessesPerformanceApiProps) => providesList(
        result?.accessPerformance?.daily,
        this.tagType,
      ),
    };
  }

  getAccesses() {
    return {
      query: ({ siteId, ...params }: AccessParamsProps) => ({
        url: `sites/${siteId}/logs`,
        params,
      }),

      providesTags: (result: AccessesApiProps | undefined) => (
        providesList(result?.accesses, this.tagType)
      ),
    };
  }

  createManualAccess() {
    return {
      query: ({
        siteId, sectionId, rut, accessDate, actionStr, comment, facilityId,
      }: CreateManualAccessParams) => ({
        url: sectionId ? `sections/${sectionId}/logs` : `sites/${siteId}/logs`,
        method: 'POST',
        body: {
          rut,
          access_date: accessDate,
          action_str: actionStr,
          facility_id: facilityId ?? null,
          ...(comment ? { comment } : {}),
        },
      }),
      invalidatesTags: [{ type: this.tagType, id: 'LIST' }],
    };
  }

  updateManualAccess() {
    return {
      query: ({
        accessId, rut, accessDate, actionStr, comment, facilityId,
      }: UpdateManualAccessParams) => ({
        url: `logs/${accessId}`,
        method: 'PUT',
        body: {
          rut,
          access_date: accessDate,
          action_str: actionStr,
          facility_id: facilityId ?? null,
          ...(comment ? { comment } : {}),
        },
      }),
      invalidatesTags: [{ type: this.tagType, id: 'LIST' }],
    };
  }

  deleteManualAccess() {
    return {
      query: ({ accessId }: DeleteManualAccessParams) => ({
        url: `logs/${accessId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: this.tagType, id: 'LIST' }],
    };
  }

  // eslint-disable-next-line class-methods-use-this
  previewManualAccess() {
    return {
      query: ({
        siteId, rut, accessDate, actionStr, facilityId,
      }: CreateManualAccessParams) => ({
        url: `sites/${siteId}/logs/preview`,
        method: 'POST',
        body: {
          rut,
          access_date: accessDate,
          action_str: actionStr,
          ...(facilityId ? { facility_id: facilityId } : {}),
        },
      }),
    };
  }

  getSectionAccesses() {
    return {
      query: ({ sectionId, ...params }: SectionAccessParamsProps) => ({
        url: `sections/${sectionId}/logs`,
        params,
      }),

      providesTags: (result: AccessesApiProps | undefined) => (
        providesList(result?.accesses, this.tagType)
      ),
    };
  }

  getAccessStaffingTable() {
    return {
      query: ({ contractorId, ...params }: AccessStaffingTableParams) => ({
        url: `contractors/${contractorId}/stats/access`,
        params,
      }),
      providesTags: (result?: AccessesStaffingTableApiProps) => providesList(
        result?.rows,
        this.tagType,
      ),
    };
  }

  getAccessByDocuments() {
    return {
      query: ({ contractorId, ...params }: AccessByDocumentParams) => ({
        url: `contractors/${contractorId}/stats/documents`,
        params,
      }),
      providesTags: [{ type: this.tagType, id: 'LIST' }],
    };
  }

  getEventsBySite() {
    return {
      query: ({ siteId, ...params }: EventsParams) => ({
        url: `sites/${siteId}/vehicle_events`,
        params,
      }),
      providesTags: (result: EventsApiProps | undefined) => (
        providesList(result?.events, this.tagType)
      ),
    };
  }

  getWorkerAccess() {
    return {
      query: ({ id, ...params }: AccessParamsProps) => ({
        url: `workers/${id}/access`,
        params,
      }),
      providesTags: (result: AccessesApiProps | undefined) => (
        providesList(result?.accesses, this.tagType)
      ),
    };
  }

  updateReferencePhoto() {
    return {
      query: ({
        siteId,
        accessId,
        sourceLogId,
      }: {
        siteId: number;
        accessId: number;
        sourceLogId: number;
      }) => ({
        url: `sites/${siteId}/logs/${accessId}/reference-photo`,
        method: 'PATCH',
        body: { source_log_id: sourceLogId },
      }),
      invalidatesTags: [{ type: this.tagType, id: 'LIST' }],
    };
  }
}

export default new Access('accesses', 'Access');
